import {
	FunctionComponent,
	ReactElement,
	useCallback,
	useContext,
	useEffect
} from "react";
import {
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter,
	createBrowserNavigationUrl
} from "client/Navigation";
import { Overview } from "./Overview/Overview";
import {
	HubDataSource,
	HubItemCategoryModel,
	HubItemDataSource
} from "client/Api/Hub/hub";
import { HubLayoutContext } from "./HubLayout";
import { CommunityRouterUrlOptions, getHubNavigationForCommunity } from "./Communities/CommunitiesPage";
import { getHubNavigationForEvent } from "./Events/EventsPage";
import { getHubNavigationForKnowledgeItem } from "./KnowledgeItems/KnowledgeItemsPage";
import { never } from "client_legacy/ndaw/errors";
import { useCategories } from "./useCategories";
import { useCanOpenHubManagement } from "./useCanOpenHubManagement";
import { HubView } from "./common";
import { getHubNavigationForAnnouncement } from "./Announcements/AnnouncementsPage";
import { hubRouting } from "./routing";

export type HubOverviewPageProps = {
	onNavigate: NavigationCallback;
};

export const HubOverviewPage = (props: HubOverviewPageProps): ReactElement | null => {
	const {
		onNavigate
	} = props;

	const {
		hierarchy,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities,
		communityCreationPermissions,
		changeActiveFilter,
		changeActiveSidebarItem
	} = useContext(HubLayoutContext);

	const categories: OptionalModel<readonly HubItemCategoryModel[]> = useCategories(hierarchy);

	const canOpenHubManagement: boolean = useCanOpenHubManagement(
		categories,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities
	);

	useEffect(
		(): void => {
			changeActiveSidebarItem({
				source: undefined
			});
		},
		[changeActiveSidebarItem]
	);

	const changeViewHandler = useCallback(
		(value: HubView): void => {
			let to: Navigation;

			switch (value.source) {
				case HubDataSource.Announcement:
					to = getHubNavigationForAnnouncement({
						source: HubDataSource.Announcement,
						itemId: value.kind === "details"
							? value.id
							: undefined
					});
					break;
				case HubDataSource.KnowledgeItem:
					to = getHubNavigationForKnowledgeItem({
						source: HubDataSource.KnowledgeItem,
						itemId: value.kind === "details"
							? value.id
							: undefined
					});
					break;
				case HubDataSource.Event:
					to = getHubNavigationForEvent({
						source: HubDataSource.Event,
						itemId: value.kind === "details"
							? value.id
							: undefined
					});
					break;
				case HubDataSource.Community:
					let options: Omit<CommunityRouterUrlOptions, "source">;

					switch (value.kind) {
						case "my-feed":
							options = { communityId: "my-feed" };
							break;
						case "community":
							options = { communityId: value.id };
							break;
						case "post":
							options = { postId: value.id };
							break;
						default:
							never(value);
					}

					to = getHubNavigationForCommunity({
						...options,
						source: HubDataSource.Community,
					});
					break;
				default:
					never(value);
			}

			onNavigate(to);
		},
		[onNavigate]
	);

	const selectCategoryHandler = useCallback(
		(source: HubItemDataSource, categoryId: number | null): void => {
			let navigation: Navigation;

			switch (source) {
				case HubDataSource.Announcement:
					navigation = getHubNavigationForAnnouncement({
						source,
						itemId: undefined,
						categoryId: categoryId ?? undefined
					});
					break;
				case HubDataSource.Event:
					navigation = getHubNavigationForEvent({
						source,
						categoryId: categoryId ?? undefined,
						itemId: undefined
					});
					break;
				case HubDataSource.KnowledgeItem:
					navigation = getHubNavigationForKnowledgeItem({
						source,
						viewIdentifier: {
							categoryId: categoryId ?? undefined,
							kind: "categoryId"
						}
					});
					break;
				default:
					never(source);
			}

			onNavigate(navigation);
		},
		[onNavigate]
	);

	if (!categories.isSet) {
		return null;
	}

	return (
		<Overview
			categories={categories.value}
			hubItemKindsCanCreateNewCategoriesFor={hubItemKindsCanCreateNewCategoriesFor}
			onViewChange={changeViewHandler}
			onActiveFilterChange={changeActiveFilter}
			onSelectCategory={selectCategoryHandler}
			canOpenHubManagement={canOpenHubManagement}
			communityCreationPermissions={communityCreationPermissions}
		/>
	);
};
HubOverviewPage.displayName = "HubOverviewPage";

export const HubOverviewPageForRouter: FunctionComponent = connectToRouter(
	HubOverviewPage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export function getHubNavigationForPage(): Navigation {
	return hubRouting.page({});
}

export function getHubUrlForPage(): string {
	return createBrowserNavigationUrl(getHubNavigationForPage());
}
